import { LeadCarDetails } from "../LeadCarDetails";
import { LeadContactInfo } from "../LeadContactInfo";
import { LeadValueEstimation } from "../LeadValueEstimation";
import { LeadPriceInfo } from "../LeadPriceInfo";
import { LeadDetails } from "@/types/lead";
import { useLead } from "@/hooks/useLead";
import { Card, CardHeader } from "@/components/ui/card";
import { LeadCardHeader } from "../LeadCardHeader";
import { LeadComments } from "../LeadComments";
import { getLanguage } from "@/utils/languageUtils";
import { leadUILabels, commonMiscLabels } from "@/i18n";

interface LeadDetailsContentProps {
  lead: LeadDetails;
}

export const LeadDetailsContent = ({ lead }: LeadDetailsContentProps) => {
  const { maskLeadAsync } = useLead();

  return (
    <Card className="glass-card p-6">
      <CardHeader className="p-2 pb-6">
        <div className="flex flex-col gap-4">
          <LeadCardHeader
            regNumber={lead.vehicleDetails?.regNumber || ""}
            make={
              lead.vehicleDetails?.make ||
              commonMiscLabels(getLanguage()).unknown
            }
            model={
              lead.vehicleDetails?.model ||
              commonMiscLabels(getLanguage()).unknown
            }
          />
        </div>
      </CardHeader>
      <div className="grid gap-4 md:grid-cols-2 backdrop-blur-md">
        <div className="space-y-4 animate-fade-up">
          <LeadContactInfo
            leadId={lead.id}
            customerName={
              lead.owner_customer
                ? `${lead.owner_customer.firstName || ""} ${
                    lead.owner_customer.lastName || ""
                  }`.trim() ||
                  lead.owner_customer.companyName ||
                  "Okänd"
                : lead.owner_guest
                  ? `${lead.owner_guest.firstName || ""} ${
                      lead.owner_guest.lastName || ""
                    }`.trim() ||
                    lead.owner_guest.companyName ||
                    "Okänd"
                  : "Okänd"
            }
            email={lead.email}
            phone={lead.phone}
            postalCode={lead.postalCode}
            isGuest={!!lead.owner_guest}
            hasOwnerInfo={!!(lead.owner_customer || lead.owner_guest)}
            onMaskLead={async (leadId: string) => {
              const uiLabels = leadUILabels(getLanguage());
              const commonLabels = commonMiscLabels(getLanguage());
              const confirmed = window.confirm(
                `${commonLabels.areYouSure} ${uiLabels.guestInfoWarning}`,
              );
              if (!confirmed) return;

              try {
                await maskLeadAsync(leadId);
              } catch (error) {}
            }}
            userPosition={lead.userPosition}
          />
          <LeadComments
            comments={lead.comments || []}
            leadId={lead.id}
            userComment={lead.userComment}
            leadCreatedAt={
              lead.createdAt
                ? typeof lead.createdAt === "string"
                  ? lead.createdAt
                  : lead.createdAt.toISOString()
                : undefined
            }
          />
        </div>

        <div className="space-y-4 animate-fade-up [animation-delay:200ms]">
          <LeadCarDetails id={lead.id} vehicleDetails={lead.vehicleDetails} />
        </div>
      </div>

      {lead.priceInfo && (
        <div className="w-full mt-6 backdrop-blur-md animate-fade-up [animation-delay:250ms]">
          <LeadPriceInfo {...lead.priceInfo} />
        </div>
      )}

      <div className="w-full mt-6 backdrop-blur-md animate-fade-up [animation-delay:300ms]">
        <LeadValueEstimation
          valueData={lead.valueEstimation.valueData}
          ratingIntervalsRaw={lead.valueEstimation.ratingIntervalsRaw}
          vehicleCardId={lead.id}
          vehicleDetails={lead.vehicleDetails}
        />
      </div>

      {/* TODO: Do we remove this? Or are we getting these from AutoUncle?
      <div className="w-full backdrop-blur-md animate-fade-up [animation-delay:500ms]">
        <LeadSimilarListings
          make={lead.vehicleDetails.make}
          model={lead.vehicleDetails.model}
          year={lead.vehicleDetails.year}
        />
      </div> */}
    </Card>
  );
};
